import { useMemo } from 'react'
import type { useQueries } from '@tanstack/react-query'
import { useMyTargetPerformanceContext } from 'pages/myTarget-performance/myTarget-performance-context'
import { useEssentials } from 'hooks/utils/useEssentials'
import { convertTimeDimensionToGFFormat } from 'pages/myTarget-performance/helpers'
import { Metric } from 'pages/myTarget-performance/types'
import { TableData, Cell } from 'utils/data'
import { MPMEnvType } from 'utils/env'
import { isCompany } from 'utils/location'
import { useMyTargetPerformanceGreenStores } from './useMyTargetPerformanceGreenStores'

export const getCompanyFooterAsRows = (
    tableData: TableData | undefined,
): TableData | undefined => {
    if (!tableData?.footer?.length) return tableData
    return {
        ...tableData,
        rows: [tableData.footer],
        footer: undefined,
    }
}

interface MetricsHookParams {
    env: MPMEnvType
    accessToken: string
    location: string
    timeDimensions: string[]
    enabled: boolean
}

interface GoalsHookParams {
    env: MPMEnvType
    accessToken: string
    location: string
    enabled: boolean
}

interface MetricsHookResult {
    dataByTimeDimension: Record<string, TableData | undefined>
    isLoading: boolean
    isError: boolean
    results?: ReturnType<typeof useQueries>
}

interface GoalsHookResult {
    data: TableData | undefined
    isLoading: boolean
    isError: boolean
    refetch: () => void
}

interface UseMyTargetPerformanceTabParams {
    useMetricsHook: (params: MetricsHookParams) => MetricsHookResult
    useGoalsHook: (params: GoalsHookParams) => GoalsHookResult
}

/**
 * Shared hook for MyTarget Performance tabs (Guest, Financial, Team).
 * Consolidates duplicate logic for fetching metrics and goals, filtering metrics,
 * and managing loading/error states.
 *
 * @param useMetricsHook - The specific metrics hook for this tab (e.g., useMyTargetPerformanceGuestMetrics)
 * @param useGoalsHook - The specific goals hook for this tab (e.g., useMyTargetPerformanceGuestGoals)
 * @returns Object containing filtered metrics, data mappings, and state flags
 */
export const useMyTargetPerformanceTab = ({
    useMetricsHook,
    useGoalsHook,
}: UseMyTargetPerformanceTabParams) => {
    const { data, isLoading, isError, error, refetch } =
        useMyTargetPerformanceContext()
    const { env, session, currentLocation } = useEssentials()

    const allTimeDimensions = useMemo(() => {
        if (!data || !Array.isArray(data)) return []
        const dimensionsSet = new Set<string>()
        data.forEach((metric: Metric) => {
            // Include the primary time dimension
            if (metric.primary_time_dimension) {
                const gfDimension = convertTimeDimensionToGFFormat(
                    metric.primary_time_dimension,
                )
                dimensionsSet.add(gfDimension)
            }
            // Include the secondary time dimension if it exists
            if (metric.secondary_time_dimension) {
                const gfDimension = convertTimeDimensionToGFFormat(
                    metric.secondary_time_dimension,
                )
                dimensionsSet.add(gfDimension)
            }
            // Include all time dimensions from the array
            metric.time_dimensions?.forEach((dim) => {
                const gfDimension = convertTimeDimensionToGFFormat(dim)
                dimensionsSet.add(gfDimension)
            })
        })
        return Array.from(dimensionsSet)
    }, [data])

    const {
        dataByTimeDimension,
        isLoading: isLoadingMetrics,
        isError: isErrorMetrics,
        results: metricsResults,
    } = useMetricsHook({
        env,
        accessToken: session?.accessToken || '',
        location: isCompany(currentLocation) ? '' : currentLocation,
        timeDimensions: allTimeDimensions,
        enabled: !isLoading && !isError && allTimeDimensions.length > 0,
    })

    const {
        data: goalsData,
        isLoading: isLoadingGoals,
        isError: isErrorGoals,
        refetch: refetchGoals,
    } = useGoalsHook({
        env,
        accessToken: session?.accessToken || '',
        location: isCompany(currentLocation) ? '' : currentLocation,
        enabled: !isLoading && !isError,
    })

    const {
        data: greenStoresData,
        isLoading: isLoadingGreenStores,
        isError: isErrorGreenStores,
        refetch: refetchGreenStores,
    } = useMyTargetPerformanceGreenStores({
        env,
        accessToken: session?.accessToken || '',
        location: currentLocation,
        enabled: !isLoading && !isError,
    })

    // Filter metrics to only show those that have matching columns in the Greenfield response
    const filteredMetrics = useMemo(() => {
        if (!data || !Array.isArray(data)) return []
        if (
            !dataByTimeDimension ||
            Object.keys(dataByTimeDimension).length === 0
        ) {
            return []
        }

        // Get available metric names from any Greenfield response
        const availableMetricNames = new Set<string>()
        Object.values(dataByTimeDimension).forEach((data) => {
            if (data?.headings) {
                data.headings.forEach((heading: Cell) => {
                    if (heading?.raw) {
                        availableMetricNames.add(heading.raw)
                    }
                })
            }
        })

        // Only include metrics that have a matching column in the Greenfield data
        return data.filter((metric: Metric) =>
            availableMetricNames.has(metric.metric_name),
        )
    }, [data, dataByTimeDimension])

    // Combined refetch function that refetches context, metrics, and goals
    const refetchAll = () => {
        refetch()
        refetchGoals()
        refetchGreenStores()
        metricsResults?.forEach((result) => result.refetch())
    }

    // At company level, use footer data instead of rows
    const companyLevel = isCompany(currentLocation)

    const adjustedDataByTimeDimension = useMemo(() => {
        if (!companyLevel) return dataByTimeDimension
        const adjusted: Record<string, TableData | undefined> = {}
        for (const key of Object.keys(dataByTimeDimension)) {
            adjusted[key] = getCompanyFooterAsRows(dataByTimeDimension[key])
        }
        return adjusted
    }, [companyLevel, dataByTimeDimension])

    const adjustedGoalsData = useMemo(
        () => (companyLevel ? getCompanyFooterAsRows(goalsData) : goalsData),
        [companyLevel, goalsData],
    )

    return {
        filteredMetrics,
        dataByTimeDimension: adjustedDataByTimeDimension,
        goalsData: adjustedGoalsData,
        greenStoresData,
        isLoading:
            isLoading ||
            isLoadingMetrics ||
            isLoadingGoals ||
            isLoadingGreenStores,
        isError:
            isError || isErrorMetrics || isErrorGoals || isErrorGreenStores,
        error,
        refetch: refetchAll,
    }
}
